import type { AuthProviderId } from "./authProviders";

export interface AuthGateInput {
  /** True once the first token-resolution attempt has completed. */
  initialResolved: boolean;
  /** Extension-embedded runtime (auth flows through the AuthBridge). */
  embedded: boolean;
  provider: AuthProviderId | null;
  /** The bridge's own loading flag (embedded Entra only). */
  bridgeLoading: boolean;
  /** The token the bridge currently holds (embedded Entra only). */
  bridgeToken: string | null;
  /** The token AuthContext has mirrored into its own state. */
  accessToken: string | null;
  /** Explicit login/logout transition flag. */
  isLoadingExplicit: boolean;
}

/**
 * Whether auth is still resolving — i.e. the app must show a spinner and render
 * NEITHER the app NOR the login screen yet. This is the fix for the "flash of
 * search bar → login prompt (or vice-versa) on open even with a valid token":
 * the UI must not decide app-vs-login until auth is definitively known.
 *
 * Two windows are covered that the old `isLoading` missed:
 *   1. The initial resolution window for ALL providers. `accessToken` is
 *      populated asynchronously; before the first resolve completes we don't yet
 *      know whether there's a valid token, so `!initialResolved` ⇒ loading.
 *      (Previously b2c/standalone reported `isLoadingExplicit=false` on mount,
 *      so ProtectedRoute redirected to /login before the stored token loaded.)
 *   2. The bridge→AuthContext MIRROR lag for embedded Entra. When the bridge has
 *      delivered its FIRST token but AuthContext hasn't mirrored it into
 *      `accessToken` yet, `isAuthenticated` is briefly false while
 *      `bridge.isLoading` is already false — a one-render window that flashed the
 *      login screen. Treat "bridge has a token but we've mirrored none yet" as
 *      still-loading.
 *   3. The embedded session-recovery window. If the shell holds a token but this
 *      iframe hasn't adopted a provider yet (localStorage cleared), we're about
 *      to adopt Entra — stay in the spinner instead of flashing /login.
 */
export function deriveIsLoading(input: AuthGateInput): boolean {
  if (!input.initialResolved) return true;

  // Embedded auth resolves through the AuthBridge, so hold the spinner until the
  // bridge has SETTLED (delivered its first token/cleared message) before ever
  // deciding app-vs-login. This applies even when the iframe hasn't adopted a
  // provider yet (`provider === null`, e.g. a fresh/cleared iframe localStorage):
  // a valid session may still be arriving over the bridge, and we must not flash
  // the login screen — which prompts a redundant sign-in — while it does. B2C
  // embedded is excluded: it authenticates directly, not through the bridge.
  const bridgeBacked =
    input.embedded &&
    (input.provider === "entra" || input.provider === null);
  if (bridgeBacked) {
    if (input.bridgeLoading) return true;
    // Bridge has delivered a token but AuthContext hasn't mirrored it yet (or
    // hasn't adopted Entra for a provider-less iframe) — keep the spinner up
    // rather than flashing login. Covers both the mirror lag and the session-
    // recovery window. A later token ROTATION (both tokens non-null but
    // different, e.g. a silent refresh) does NOT spin — it mirrors seamlessly.
    if (input.bridgeToken && !input.accessToken) return true;
    return false; // bridge settled: token mirrored → app; no token → login
  }

  return input.isLoadingExplicit;
}
